import React, { useState } from 'react';
import Menu, { TriggerProps } from '../../Menu/Menu';
import MenuItem from '../../Menu/MenuItem';
import shareIcon from '../../../../static/icons/share.svg';
import followIcon from '../../../../static/icons/follow.svg';
import trashIcon from '../../../../static/icons/trash.svg';
import exportIcon from '../../../../static/icons/download.svg';
import pdfIcon from '../../../../static/icons/documentsType/pdf.svg';
import excelIcon from '../../../../static/icons/documentsType/excel.svg';
import CellTrigger from '../../Menu/MenuTriggers/CellTrigger';
import { connect } from 'react-redux';
import { RootState } from '../../../../state/store/store';
import { exportTypes } from '../../../../state/ducks/allPurchaseOrdersPage/allPurchaseOrdersPage';
import { localizationSelectors } from '../../../../state/ducks/localization/selectors';
import { userSelectors } from '../../../../state/ducks/user/selectors';
import { PagesRoutes } from '../../../../routing/PagesRoutes';
import CustomModal, { ModalTriggerProps } from '../../Modal/Modal';
import InviteUserFollowPurchaseOrderForm from '../../InviteUserFollowPurchaseOrderForm/InviteUserFollowPurchaseOrderForm';
import { PDFViewer } from '@react-pdf/renderer';
import PurchaseOrderPdfDocument from '../../../pages/singlePurchaseOrder/PurchaseOrderPdfDocument';
import { purchaseOrder } from '../../../../models/entities/purchaseOrder/purchaseOrder';

type exportDataRowProps = (purchaseOrderId: string, type: exportTypes, fileName: string) => void;
type Props = {
    purchaseOrder: purchaseOrder;
    entityId: string;
    entityData: { CargoZoneNumber: string };
    localization: any;
    singlePOLocalization: any;
    exportDataRow: exportDataRowProps;
    trigger?: (props: TriggerProps) => JSX.Element;
    deletePurchaseOrder: (purchaseOrderId: string) => Promise<any>;
    onDelete?: () => void;
    followPurchaseOrder: (purchaseOrderId: string) => void;
    unFollowPurchaseOrder: (purchaseOrderId: string) => void;
    followingPurchaseOrders?: Array<string>;
    setIsModalOpened?: (isOpened: boolean) => void;
};

const PurchaseOrderMenu = ({
    entityId,
    entityData,
    localization,
    singlePOLocalization,
    exportDataRow,
    deletePurchaseOrder,
    trigger,
    onDelete,
    followPurchaseOrder,
    unFollowPurchaseOrder,
    followingPurchaseOrders,
    purchaseOrder
}: Props) => {
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [pdfModalVisible, setPdfModalVisible] = useState(false);
    const [pdfNoPricesModalVisible, setPdfNoPricesModalVisible] = useState(false);
    const [inviteModalVisible, setInviteModalVisible] = useState(false);
    
    const isUserFollowing = followingPurchaseOrders?.includes(entityId);

    const renderPdfItem = (title: string, hidePrices?: boolean) => {
        const isNoPrices = hidePrices === true;
        const modalVisible = isNoPrices ? pdfNoPricesModalVisible : pdfModalVisible;
        const setModalVisible = isNoPrices ? setPdfNoPricesModalVisible : setPdfModalVisible;
        
        return (
            <CustomModal
                isOpen={modalVisible}
                title={{ text: title }}
                Trigger={(props: ModalTriggerProps) => (
                    <MenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsModalOpened(true);
                            setModalVisible(true);
                        }}
                        icon={exportIcon}
                        text={title}
                    />
                )}
                onCancelCallback={() => {
                    setIsModalOpened(false);
                    setModalVisible(false);
                }}
                Content={() => {
                    return (
                        <PDFViewer width="100%" height="100%">
                            <PurchaseOrderPdfDocument
                                purchaseOrder={purchaseOrder}
                                localization={singlePOLocalization}
                                allPurchaseOrdersLocalization={localization}
                                hidePrices={hidePrices}
                            />
                        </PDFViewer>
                    );
                }}
                width="90%"
                maxWidth="90%"
                fullScreen
            />
        );
    };

    const menuItems = [
        <MenuItem
            onClick={() => {
                isUserFollowing ? unFollowPurchaseOrder(entityId) : followPurchaseOrder(entityId);
            }}
            icon={followIcon}
            text={isUserFollowing ? localization.grid.menu.unfollow : localization.grid.menu.follow}
        />,
        <MenuItem icon={shareIcon} text={localization.grid.menu.share} />,
        <Menu
            position="left-center"
            options={[renderPdfItem(localization.grid.menu.pdf), renderPdfItem(localization.grid.menu.pdf_no_prices, true)]}
            MenuTrigger={(props) => (
                <MenuItem
                    onClick={(e) => {
                        props.onClick(e);
                    }}
                    icon={exportIcon}
                    text={localization.grid.menu.export}
                />
            )}
        />,
        <CustomModal
            isOpen={inviteModalVisible}
            title={{ text: localization.grid.menu.invite_to_follow, icon: followIcon }}
            Trigger={(props: ModalTriggerProps) => (
                <MenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsModalOpened(true);
                        setInviteModalVisible(true);
                    }}
                    icon={followIcon}
                    text={localization.grid.menu.invite_to_follow}
                />
            )}
            onCancelCallback={() => {
                setIsModalOpened(false);
                setInviteModalVisible(false);
            }}
            Content={(setVisible?: React.Dispatch<React.SetStateAction<boolean>>, setConfirm?: React.Dispatch<React.SetStateAction<boolean>>) => {
                return (
                    <InviteUserFollowPurchaseOrderForm
                        purchaseOrderId={entityId}
                        setModalVisible={(visible: boolean) => {
                            setVisible && setVisible(visible);
                            setIsModalOpened(visible);
                        }}
                        confirmation={setConfirm ? { setConfirm } : undefined}
                    ></InviteUserFollowPurchaseOrderForm>
                );
            }}
            width="393px"
            maxWidth="393px"
        />,
        <MenuItem
            onClick={() => {
                deletePurchaseOrder(entityId).then(() => {
                    if (onDelete) onDelete();
                });
            }}
            icon={trashIcon}
            text={localization.grid.menu.delete}
            requiredConfirm
        />
    ];

    return (
        <Menu
            menuWidth={165}
            options={menuItems}
            isModalOpened={isModalOpened}
            MenuTrigger={trigger || CellTrigger}
            strStyle="display: flex;align-items: center;width: 100%;height: 100%;"
        />
    );
};

const mapStateToProps = (state: RootState) => ({
    localization: localizationSelectors.allPurchaseOrdersPage(state),
    singlePOLocalization: localizationSelectors.singlePurchaseOrderPage(state),
    followingPurchaseOrders: userSelectors.followingPurchaseOrders(state)
});

const mapDispatchToProps = (dispatch: any) => ({
    exportDataRow: (purchaseOrderId: string, type: exportTypes, fileName: string) =>
        dispatch.allPurchaseOrdersPage.exportDataRow({ purchaseOrderId, type, fileName }),
    deletePurchaseOrder: async (purchaseOrderId: string) => dispatch.allPurchaseOrdersPage.deletePurchaseOrder(purchaseOrderId),
    followPurchaseOrder: async (purchaseOrderId: string) => dispatch.allPurchaseOrdersPage.followPurchaseOrder(purchaseOrderId),
    unFollowPurchaseOrder: async (purchaseOrderId: string) => dispatch.allPurchaseOrdersPage.unFollowPurchaseOrder(purchaseOrderId)
});

export default connect(mapStateToProps, mapDispatchToProps)(PurchaseOrderMenu);